/**
 * SecureEmbed Management Portal
 *
 * Main application component: routing, layout, and session-managed authentication.
 *
 * The sidebar navigation is defined by the `navigation` array below (see the
 * Management Portal redesign plan, documentation/…Redesign…Plan.docx, for the
 * target grouped IA this is migrating toward). Elevated routes gate on the
 * Cognito 'Admins' group via ProtectedRoute, matching the backend handlers.
 */

import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';

import { AppErrorBoundary } from '@secureembed/shared-crm';
import { lazyWithRetry } from './utils/lazyWithRetry';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ScopeProvider } from './contexts/ScopeContext';
import { EnvironmentProvider } from './contexts/EnvironmentContext';
import { ModelConfigPortalProvider } from './contexts/ModelConfigPortalContext';
import Layout from './components/layout/Layout';
import { navGroups, allNavHrefs as sidebarNavHrefs } from './components/layout/nav';
import { isW1Shell } from './utils/w1Flag';

// Import pages
import Dashboard from './pages/Dashboard';
// DeploymentHub removed — /deploy redirects to /
import Monitor from './pages/Monitor';
// DataIngestion removed — replaced by IngestionWizard + IngestionJobs
import SettingsPage from './pages/Settings';
import Login from './pages/Login'
import Forbidden from './pages/Forbidden';
import NotFound from './pages/NotFound';
import ProfilePage from './pages/Profile';
import PreferencesPage from './pages/Preferences';
// IngestionMonitor deprecated — replaced by IngestionActivity
import IngestionActivity from './pages/IngestionActivity';
import IngestionControl from './pages/IngestionControl';
// CreateIngestionJob removed — replaced by IngestionWizard
import IngestionWizard from './pages/IngestionWizard';
import IngestionJobs from './pages/IngestionJobs';
import IngestionJobDetail from './pages/IngestionJobDetail';
import AemoIngestion from './pages/AemoIngestion';
// AemoDatastore, AerDatastore, BomDatastore removed — sub-pages consolidated into /datastores
import AccessUsers from './pages/AccessUsers';
import AccessControl from './pages/AccessControl';
import PortalUsersPage from './pages/PortalUsers';
import PricingConfiguration from './pages/PricingConfiguration';

// Lazy-loaded pages
const DatastoreBrowser = lazyWithRetry(() => import('./pages/DatastoreBrowser'), './pages/DatastoreBrowser');
const OrganisationDetail = lazyWithRetry(() => import('./components/organizations/OrganisationDetail'), './components/organizations/OrganisationDetail');
const DataStoresPage = lazyWithRetry(() => import('./pages/DataStores'), './pages/DataStores');
const PlatformServices = lazyWithRetry(() => import('./pages/PlatformServices'), './pages/PlatformServices');
const ServiceDetail = lazyWithRetry(() => import('./pages/ServiceDetail'), './pages/ServiceDetail');
const BackupRestore = lazyWithRetry(() => import('./pages/BackupRestore'), './pages/BackupRestore');
const BackupCatalogue = lazyWithRetry(() => import('./pages/BackupCatalogue'), './pages/BackupCatalogue');
const VaultsDR = lazyWithRetry(() => import('./pages/VaultsDR'), './pages/VaultsDR');
const KeyManagement = lazyWithRetry(() => import('./pages/KeyManagement'), './pages/KeyManagement');
const KnowledgeHome = lazyWithRetry(() => import('./pages/knowledge/Home'), './pages/knowledge/Home');
const KnowledgeArchitecture = lazyWithRetry(() => import('./pages/knowledge/Architecture'), './pages/knowledge/Architecture');
const KnowledgeDecisions = lazyWithRetry(() => import('./pages/knowledge/Decisions'), './pages/knowledge/Decisions');
const KnowledgeDiagrams = lazyWithRetry(() => import('./pages/knowledge/Diagrams'), './pages/knowledge/Diagrams');
const KnowledgeExports = lazyWithRetry(() => import('./pages/knowledge/Exports'), './pages/knowledge/Exports');
const KnowledgeHelpRecords = lazyWithRetry(() => import('./pages/knowledge/HelpRecords'), './pages/knowledge/HelpRecords');
const KnowledgeRunbooks = lazyWithRetry(() => import('./pages/knowledge/Runbooks'), './pages/knowledge/Runbooks');
const KnowledgeGuides = lazyWithRetry(() => import('./pages/knowledge/Guides'), './pages/knowledge/Guides');
const KnowledgeEvidence = lazyWithRetry(() => import('./pages/knowledge/Evidence'), './pages/knowledge/Evidence');
const ContactsList = lazyWithRetry(() => import('./pages/ContactsList'), './pages/ContactsList');
const ContactDetail = lazyWithRetry(() => import('./pages/ContactDetail'), './pages/ContactDetail');
const CRMDashboard = lazyWithRetry(() => import('./pages/CRMDashboard'), './pages/CRMDashboard');
const DocumentsPage = lazyWithRetry(() => import('./pages/DocumentsPage'), './pages/DocumentsPage');
const SharingPage = lazyWithRetry(() => import('./pages/SharingPage'), './pages/SharingPage');
const AccessControlDetail = lazyWithRetry(() => import('./pages/AccessControlDetail'), './pages/AccessControlDetail');
const Logs = lazyWithRetry(() => import('./pages/Logs'), './pages/Logs');
const OperationsAudit = lazyWithRetry(() => import('./pages/operations/Audit'), './pages/operations/Audit');
const DemoHolds = lazyWithRetry(() => import('./pages/operations/demo/Holds'), './pages/operations/demo/Holds');
const DemoScenarios = lazyWithRetry(() => import('./pages/operations/demo/Scenarios'), './pages/operations/demo/Scenarios');
const DemoReadiness = lazyWithRetry(() => import('./pages/operations/demo/Readiness'), './pages/operations/demo/Readiness');
const DemoResets = lazyWithRetry(() => import('./pages/operations/demo/Resets'), './pages/operations/demo/Resets');
const DemoEvidence = lazyWithRetry(() => import('./pages/operations/demo/Evidence'), './pages/operations/demo/Evidence');
const Portals = lazyWithRetry(() => import('./pages/Portals'), './pages/Portals');
const PortalDeployments = lazyWithRetry(() => import('./pages/PortalDeployments'), './pages/PortalDeployments');
const PortalSettings = lazyWithRetry(() => import('./pages/PortalSettings'), './pages/PortalSettings');
// ModelConfiguration.tsx stays on disk (Story #5346 Stream 1c keeps the
// file per its task spec) but is no longer routed to directly -- /model-config
// now redirects to /administration/ai-model-defaults (A20), so this lazy
// import is intentionally removed rather than left dead.
const ConfigurationAiModels = lazyWithRetry(() => import('./pages/ConfigurationAiModels'), './pages/ConfigurationAiModels');
const AiModelCompare = lazyWithRetry(() => import('./pages/AiModelCompare'), './pages/AiModelCompare');
const AiModelDefaults = lazyWithRetry(() => import('./pages/AiModelDefaults'), './pages/AiModelDefaults');
const ConfigurationPlansPricing = lazyWithRetry(() => import('./pages/ConfigurationPlansPricing'), './pages/ConfigurationPlansPricing');
const AdminPricing = lazyWithRetry(() => import('./pages/AdminPricing'), './pages/AdminPricing');
const ConfigurationGeneral = lazyWithRetry(() => import('./pages/ConfigurationGeneral'), './pages/ConfigurationGeneral');
const ConfigurationIntegrations = lazyWithRetry(() => import('./pages/ConfigurationIntegrations'), './pages/ConfigurationIntegrations');
const UsageCostSummary = lazyWithRetry(() => import('./pages/UsageCostSummary'), './pages/UsageCostSummary');
const UsageCostTrajectory = lazyWithRetry(() => import('./pages/UsageCostTrajectory'), './pages/UsageCostTrajectory');
const AdminCostExplorer = lazyWithRetry(() => import('./pages/AdminCostExplorer'), './pages/AdminCostExplorer');
const UsageCostAllocation = lazyWithRetry(() => import('./pages/UsageCostAllocation'), './pages/UsageCostAllocation');
const UsageCostAiRoles = lazyWithRetry(() => import('./pages/UsageCostAiRoles'), './pages/UsageCostAiRoles');
const UsageCostInvestigations = lazyWithRetry(() => import('./pages/UsageCostInvestigations'), './pages/UsageCostInvestigations');
const AdminBudgets = lazyWithRetry(() => import('./pages/AdminBudgets'), './pages/AdminBudgets');
const AdminMetering = lazyWithRetry(() => import('./pages/AdminMetering'), './pages/AdminMetering');
const EnergyOrganisations = lazyWithRetry(() => import('./pages/EnergyOrganisations'), './pages/EnergyOrganisations');
const DeveloperVMs = lazyWithRetry(() => import('./pages/DeveloperVMs'), './pages/DeveloperVMs');
const DeveloperVMDetail = lazyWithRetry(() => import('./pages/DeveloperVMDetail'), './pages/DeveloperVMDetail');
// RT-18: EnvironmentControl is fully superseded (baselines/pull/promote live in
// EnvironmentDetail + ReleasesHub) — /environment-control now redirects to
// /environments. The page file is kept for history/reference only.
const EnvironmentsHub = lazyWithRetry(() => import('./pages/EnvironmentsHub'), './pages/EnvironmentsHub');
const EnvironmentDetail = lazyWithRetry(() => import('./pages/EnvironmentDetail'), './pages/EnvironmentDetail');
const ReleasesHub = lazyWithRetry(() => import('./pages/ReleasesHub'), './pages/ReleasesHub');
// Story #5483 (Launch W6) Stream 1.1: rollback flag for the new /environments
// and /releases nested-route workspace. import.meta.env.VITE_* is inlined by
// Vite at build time (unlike w1Flag.ts's runtime SECUREEMBED_CONFIG read), so
// a module-scope read here is safe -- see VITE_API_BASE_URL in services/api.ts
// for the existing precedent. Off by default; Terraform sets the deployed
// value (00-dashboard hosting module), never hand-edited into config.js.
const ENV_RELEASES_V2 = import.meta.env.VITE_ENV_RELEASES_V2 === 'true';
// EnvReleasesWorkspacePlaceholder is gone from /releases (Story #5483 W6
// Stream 7): ReleaseCatalogue is a real screen now, not a placeholder. The
// page file itself is kept -- other unbuilt ENV_RELEASES_V2 routes may
// still reference it.
const EnvironmentFleet = lazyWithRetry(() => import('./pages/environments/EnvironmentFleet'), './pages/environments/EnvironmentFleet');
const EnvironmentCompare = lazyWithRetry(() => import('./pages/environments/EnvironmentCompare'), './pages/environments/EnvironmentCompare');
const EnvironmentPlanNew = lazyWithRetry(() => import('./pages/environments/EnvironmentPlanNew'), './pages/environments/EnvironmentPlanNew');
const EnvironmentLifecycle = lazyWithRetry(() => import('./pages/environments/EnvironmentLifecycle'), './pages/environments/EnvironmentLifecycle');
// Story #5483 (Launch W6) Stream 5: E13-E15 per-environment projections.
const EnvironmentDemoState = lazyWithRetry(() => import('./pages/environments/EnvironmentDemoState'), './pages/environments/EnvironmentDemoState');
const EnvironmentRecovery = lazyWithRetry(() => import('./pages/environments/EnvironmentRecovery'), './pages/environments/EnvironmentRecovery');
const EnvironmentVerification = lazyWithRetry(() => import('./pages/environments/EnvironmentVerification'), './pages/environments/EnvironmentVerification');
// Story #5483 (Launch W6) Stream 6: E16 activity, E17+E18 drift/evidence
// (the latter one route-backed component switching on ?view=).
const EnvironmentActivity = lazyWithRetry(() => import('./pages/environments/EnvironmentActivity'), './pages/environments/EnvironmentActivity');
const EnvironmentDrift = lazyWithRetry(() => import('./pages/environments/EnvironmentDrift'), './pages/environments/EnvironmentDrift');
// Story #5483 (Launch W6) Stream 2/3: E04-E11 per-environment projections.
const EnvironmentOverview = lazyWithRetry(() => import('./pages/environments/EnvironmentOverview'), './pages/environments/EnvironmentOverview');
const EnvironmentServices = lazyWithRetry(() => import('./pages/environments/EnvironmentServices'), './pages/environments/EnvironmentServices');
const EnvironmentServiceDetail = lazyWithRetry(() => import('./pages/environments/EnvironmentServiceDetail'), './pages/environments/EnvironmentServiceDetail');
const EnvironmentData = lazyWithRetry(() => import('./pages/environments/EnvironmentData'), './pages/environments/EnvironmentData');
const EnvironmentReleasesTab = lazyWithRetry(() => import('./pages/environments/EnvironmentReleasesTab'), './pages/environments/EnvironmentReleasesTab');
const EnvironmentOperationsTab = lazyWithRetry(() => import('./pages/environments/EnvironmentOperationsTab'), './pages/environments/EnvironmentOperationsTab');
const EnvironmentCostTab = lazyWithRetry(() => import('./pages/environments/EnvironmentCostTab'), './pages/environments/EnvironmentCostTab');
const EnvironmentAccessTab = lazyWithRetry(() => import('./pages/environments/EnvironmentAccessTab'), './pages/environments/EnvironmentAccessTab');
// Story #5484 (Launch W7) Stream 1: C02/C03 canonical Customers screens.
const CustomerOrganisations = lazyWithRetry(() => import('./pages/customers/Organisations'), './pages/customers/Organisations');
const CustomerContacts = lazyWithRetry(() => import('./pages/customers/Contacts'), './pages/customers/Contacts');
const CustomerDocuments = lazyWithRetry(() => import('./pages/customers/Documents'), './pages/customers/Documents');
// Story #5484 (Launch W7) Stream 4, Task #5543: C06 Customer Sharing (approval-gated).
const CustomerSharing = lazyWithRetry(() => import('./pages/customers/Sharing'), './pages/customers/Sharing');
// Story #5484 (Launch W7) Stream 2, Task #5541: C01/C04 canonical Customers screens.
const CustomerHome = lazyWithRetry(() => import('./pages/customers/CustomerHome'), './pages/customers/CustomerHome');
const CustomerInteractions = lazyWithRetry(() => import('./pages/customers/Interactions'), './pages/customers/Interactions');
// Story #5483 (Launch W6) Stream 7: R01-R04 release catalogue/create/summary/ChangeSet.
const ReleaseCatalogue = lazyWithRetry(() => import('./pages/releases/ReleaseCatalogue'), './pages/releases/ReleaseCatalogue');
const ReleaseCreate = lazyWithRetry(() => import('./pages/releases/ReleaseCreate'), './pages/releases/ReleaseCreate');
const ReleaseSummary = lazyWithRetry(() => import('./pages/releases/ReleaseSummary'), './pages/releases/ReleaseSummary');
const ReleaseChangeSet = lazyWithRetry(() => import('./pages/releases/ReleaseChangeSet'), './pages/releases/ReleaseChangeSet');
// Story #5483 (Launch W6) Stream 10: R11-R14 read-only artifact catalogues.
const ReleaseArtifactsConfig = lazyWithRetry(() => import('./pages/releases/ReleaseArtifactsConfig'), './pages/releases/ReleaseArtifactsConfig');
const ReleaseArtifactsData = lazyWithRetry(() => import('./pages/releases/ReleaseArtifactsData'), './pages/releases/ReleaseArtifactsData');
const ReleaseArtifactsProfiles = lazyWithRetry(() => import('./pages/releases/ReleaseArtifactsProfiles'), './pages/releases/ReleaseArtifactsProfiles');
const ReleaseArtifactsProvenance = lazyWithRetry(() => import('./pages/releases/ReleaseArtifactsProvenance'), './pages/releases/ReleaseArtifactsProvenance');
// Story #5483 (Launch W6) Stream 8: R05 gates, R06 approvals.
const ReleaseGates = lazyWithRetry(() => import('./pages/releases/ReleaseGates'), './pages/releases/ReleaseGates');
const ReleaseApprovals = lazyWithRetry(() => import('./pages/releases/ReleaseApprovals'), './pages/releases/ReleaseApprovals');
const ReleaseRollout = lazyWithRetry(() => import('./pages/releases/ReleaseRollout'), './pages/releases/ReleaseRollout');
const ReleaseVerification = lazyWithRetry(() => import('./pages/releases/ReleaseVerification'), './pages/releases/ReleaseVerification');
const ReleaseRollback = lazyWithRetry(() => import('./pages/releases/ReleaseRollback'), './pages/releases/ReleaseRollback');
const ReleaseHistory = lazyWithRetry(() => import('./pages/releases/ReleaseHistory'), './pages/releases/ReleaseHistory');
const PromotionPackages = lazyWithRetry(() => import('./pages/PromotionPackages'), './pages/PromotionPackages');
// W4: consolidated tabbed hubs (each renders one PageHeader + a tab bar + the
// embedded sibling pages). The old sibling routes are kept as deep-links.
const BackupsHub = lazyWithRetry(() => import('./pages/BackupsHub'), './pages/BackupsHub');
const IngestionHub = lazyWithRetry(() => import('./pages/IngestionHub'), './pages/IngestionHub');
const CostsPricingHub = lazyWithRetry(() => import('./pages/CostsPricingHub'), './pages/CostsPricingHub');
const UsersOrgsHub = lazyWithRetry(() => import('./pages/UsersOrgsHub'), './pages/UsersOrgsHub');
const SupportTickets = lazyWithRetry(() => import('./pages/SupportTickets'), './pages/SupportTickets');
// W1 (Story #5351, flag-gated): /home workspace. Lazy like every other hub page.
const HomeWorkspace = lazyWithRetry(() => import('./pages/home/HomeWorkspace'), './pages/home/HomeWorkspace');
const AttentionDetail = lazyWithRetry(() => import('./pages/home/AttentionDetail'), './pages/home/AttentionDetail');
// W1 (Story #5351 W1-T16, Screen Contract G03): /search full-screen search.
const SearchCommand = lazyWithRetry(() => import('./pages/SearchCommand'), './pages/SearchCommand');
// W1 (Story #5351 W1-T18, Stream 8): /dev/fixtures state-fixtures route.
const Fixtures = lazyWithRetry(() => import('./pages/dev/Fixtures'), './pages/dev/Fixtures');
// W3 (Story #5353, Streams 2-5): Administration > Identity screens A02-A05.
const AdministrationIdentityUsers = lazyWithRetry(() => import('./pages/AdministrationIdentityUsers'), './pages/AdministrationIdentityUsers');
const AdministrationIdentityInvitations = lazyWithRetry(() => import('./pages/AdministrationIdentityInvitations'), './pages/AdministrationIdentityInvitations');
const AdministrationIdentityAccess = lazyWithRetry(() => import('./pages/AdministrationIdentityAccess'), './pages/AdministrationIdentityAccess');
const AdministrationIdentityReviews = lazyWithRetry(() => import('./pages/AdministrationIdentityReviews'), './pages/AdministrationIdentityReviews');
const AdministrationIdentityProviders = lazyWithRetry(() => import('./pages/AdministrationIdentityProviders'), './pages/AdministrationIdentityProviders');
const AdministrationIdentityServiceIdentities = lazyWithRetry(() => import('./pages/AdministrationIdentityServiceIdentities'), './pages/AdministrationIdentityServiceIdentities');
// Story #5484 Streams 7-8: Administration > Overview (A01) and Security A12-A13.
const AdministrationOverview = lazyWithRetry(() => import('./pages/AdministrationOverview'), './pages/AdministrationOverview');
const AdministrationSecurityPosture = lazyWithRetry(() => import('./pages/AdministrationSecurityPosture'), './pages/AdministrationSecurityPosture');
const AdministrationSecurityPolicies = lazyWithRetry(() => import('./pages/AdministrationSecurityPolicies'), './pages/AdministrationSecurityPolicies');
const AdministrationSecurityKeys = lazyWithRetry(() => import('./pages/AdministrationSecurityKeys'), './pages/AdministrationSecurityKeys');
const AdministrationSecurityBreakGlass = lazyWithRetry(() => import('./pages/AdministrationSecurityBreakGlass'), './pages/AdministrationSecurityBreakGlass');
// Story #5484 (Launch W7) Stream 9: Administration > Platform settings screens A16-A19.
const AdministrationPlatformRegistries = lazyWithRetry(() => import('./pages/administration/platform/Registries'), './pages/administration/platform/Registries');
const AdministrationPlatformEnvironmentTemplates = lazyWithRetry(() => import('./pages/administration/platform/EnvironmentTemplates'), './pages/administration/platform/EnvironmentTemplates');
const AdministrationPlatformFeatureFlags = lazyWithRetry(() => import('./pages/administration/platform/FeatureFlags'), './pages/administration/platform/FeatureFlags');
const AdministrationPlatformManagedWriters = lazyWithRetry(() => import('./pages/administration/platform/ManagedWriters'), './pages/administration/platform/ManagedWriters');
// Story #5484 (Launch W7) Stream 10: Administration > Developer workspaces / Integrations A21/A22.
const AdministrationDeveloperWorkspaces = lazyWithRetry(() => import('./pages/administration/DeveloperWorkspaces'), './pages/administration/DeveloperWorkspaces');
const AdministrationIntegrations = lazyWithRetry(() => import('./pages/administration/Integrations'), './pages/administration/Integrations');
// Story #5484 (Launch W7) Stream 5, Task #5544: C07 Support + C08 Access
// projection (Customers domain).
const CustomerSupport = lazyWithRetry(() => import('./pages/customers/Support'), './pages/customers/Support');
const CustomerAccessProjection = lazyWithRetry(() => import('./pages/customers/AccessProjection'), './pages/customers/AccessProjection');
const PortalAccessUsers = lazyWithRetry(() => import('./pages/PortalAccessUsers'), './pages/PortalAccessUsers');
const PortalAccessOrganisations = lazyWithRetry(() => import('./pages/PortalAccessOrganisations'), './pages/PortalAccessOrganisations');
const PortalAccessInvitations = lazyWithRetry(() => import('./pages/PortalAccessInvitations'), './pages/PortalAccessInvitations');
const PortalAccessServiceIdentities = lazyWithRetry(() => import('./pages/PortalAccessServiceIdentities'), './pages/PortalAccessServiceIdentities');
const MyAccountPreferences = lazyWithRetry(() => import('./pages/MyAccountPreferences'), './pages/MyAccountPreferences');
const MyAccountProfile = lazyWithRetry(() => import('./pages/MyAccountProfile'), './pages/MyAccountProfile');
const MyAccountSecurity = lazyWithRetry(() => import('./pages/MyAccountSecurity'), './pages/MyAccountSecurity');
const MyAccountSessions = lazyWithRetry(() => import('./pages/MyAccountSessions'), './pages/MyAccountSessions');
const MyAccountNotifications = lazyWithRetry(() => import('./pages/MyAccountNotifications'), './pages/MyAccountNotifications');
const MyAccountEffectiveAccess = lazyWithRetry(() => import('./pages/MyAccountEffectiveAccess'), './pages/MyAccountEffectiveAccess');
// W5 Stream 1 (Story #5352, Tasks #5424-#5430): Operations O01-O06.
const CommandCentre = lazyWithRetry(() => import('./pages/operations/CommandCentre'), './pages/operations/CommandCentre');
const Attention = lazyWithRetry(() => import('./pages/operations/Attention'), './pages/operations/Attention');
const Jobs = lazyWithRetry(() => import('./pages/operations/Jobs'), './pages/operations/Jobs');
const Approvals = lazyWithRetry(() => import('./pages/operations/Approvals'), './pages/operations/Approvals');
const Schedules = lazyWithRetry(() => import('./pages/operations/Schedules'), './pages/operations/Schedules');
const Incidents = lazyWithRetry(() => import('./pages/operations/Incidents'), './pages/operations/Incidents');
// W5 Stream 2 (Story #5352, Tasks #5434-#5438): Data Flows O07-O11.
const DataFlowsOverview = lazyWithRetry(() => import('./pages/operations/data-flows/Overview'), './pages/operations/data-flows/Overview');
const DataFlowsJobs = lazyWithRetry(() => import('./pages/operations/data-flows/Jobs'), './pages/operations/data-flows/Jobs');
const DataFlowsSources = lazyWithRetry(() => import('./pages/operations/data-flows/Sources'), './pages/operations/data-flows/Sources');
const DataFlowsSchedules = lazyWithRetry(() => import('./pages/operations/data-flows/Schedules'), './pages/operations/data-flows/Schedules');
const DataFlowsActivity = lazyWithRetry(() => import('./pages/operations/data-flows/Activity'), './pages/operations/data-flows/Activity');
// W5 Stream 3 (Story #5352, Tasks #5441-#5447): Recovery O12-O17.
const RestorePlans = lazyWithRetry(() => import('./pages/operations/recovery/RestorePlans'), './pages/operations/recovery/RestorePlans');
const RecoveryRehearsals = lazyWithRetry(() => import('./pages/operations/recovery/Rehearsals'), './pages/operations/recovery/Rehearsals');
const RecoveryEvidence = lazyWithRetry(() => import('./pages/operations/recovery/Evidence'), './pages/operations/recovery/Evidence');
const PortalEnvironmentSelect = lazyWithRetry(() => import('./pages/PortalEnvironmentSelect'), './pages/PortalEnvironmentSelect');
const PortalOverview = lazyWithRetry(() => import('./pages/PortalOverview'), './pages/PortalOverview');
const PortalConfigurationEffective = lazyWithRetry(() => import('./pages/PortalConfigurationEffective'), './pages/PortalConfigurationEffective');
const PortalDocumentation = lazyWithRetry(() => import('./pages/PortalDocumentation'), './pages/PortalDocumentation');
const PortalEnvironments = lazyWithRetry(() => import('./pages/PortalEnvironments'), './pages/PortalEnvironments');
const PortalHistory = lazyWithRetry(() => import('./pages/PortalHistory'), './pages/PortalHistory');

const portalsWorkspaceHrefs = [
  '/portals/innovateflow/select',
  '/portals/innovateflow/overview',
  '/portals/innovateflow/environments',
  '/portals/innovateflow/deployments',
  '/portals/innovateflow/configuration/effective',
  '/portals/innovateflow/documentation',
  '/portals/innovateflow/history',
];

// Route-manifest consumers need the workspace's typed/deep-link destinations as
// well as the links rendered in the sidebar. /portals itself is already present
// in sidebarNavHrefs, so only the seven portal-scoped routes are appended here.
const allNavHrefs = [...sidebarNavHrefs, ...portalsWorkspaceHrefs];

const isPortalsWorkspaceEnabled = (): boolean => (
  (window.SECUREEMBED_CONFIG as typeof window.SECUREEMBED_CONFIG & {
    PORTALS_WORKSPACE_ENABLED?: boolean;
  })?.PORTALS_WORKSPACE_ENABLED === true
);

// PortalsWorkspacePlaceholder is gone: every Portals workspace route (W2,
// story #5344) now renders its real screen — the last placeholder consumer
// was /portals/:portalId/history, replaced by PortalHistory in this change.

// =============================================================================
// REACT QUERY CLIENT
// =============================================================================

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});

// =============================================================================
// NAVIGATION / LAYOUT — moved to components/layout/{nav,Sidebar,Header,Layout}
// (W1 S0b). Re-exported below for compatibility: route-manifest.test.tsx imports
// navGroups/allNavHrefs from '../App'.
// =============================================================================


// =============================================================================
// PROTECTED ROUTE - Enhanced with proper auth checking and loading states
// =============================================================================

export const ProtectedRoute: React.FC<{
  children: React.ReactNode;
  requiredGroup?: string | string[];
}> = ({
  children,
  requiredGroup,
}) => {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-iflow-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-iflow-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-500 text-sm">Verifying authentication...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // W0-2: elevated routes gate on the Cognito group that actually exists and is
  // populated — 'Admins' — which is exactly what the backend handlers enforce
  // (e.g. demo-env require_auth(require_admin=True) checks 'Admins'). The old code
  // gated on a 'platform-admin' group nobody is in and then bypassed it for
  // 'Admins', so the label was dead and the real gate was implicit. Checking the
  // required group directly (no blanket bypass) keeps front-end and back-end in
  // lockstep and lets a genuine second tier (a real platform-admin group for T2 /
  // prod destructive actions) be enforced correctly when it lands in W2/W3.
  const hasRequiredGroup = Array.isArray(requiredGroup)
    ? requiredGroup.some((group) => user?.groups?.includes(group))
    : !requiredGroup || user?.groups?.includes(requiredGroup);

  if (!hasRequiredGroup) {
    return <Navigate to="/forbidden" replace />;
  }

  return <>{children}</>;
};

// =============================================================================
// APP COMPONENT - Main application with AuthProvider
// =============================================================================

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
        <ScopeProvider>
        <EnvironmentProvider>
        <ModelConfigPortalProvider>
          <Routes>
            {/* Public route - Login (only accessible when NOT authenticated) */}
            <Route path="/login" element={<Login />} />
            <Route path="/forbidden" element={<Forbidden />} />

            {/* Protected routes - ALL require authentication */}
            <Route path="/" element={
              <ProtectedRoute>
                {isW1Shell() ? <Navigate to="/home" replace /> : <Layout><Dashboard /></Layout>}
              </ProtectedRoute>
            } />
            <Route path="/settings" element={
              <ProtectedRoute>
                <Layout><SettingsPage /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/monitor" element={
              <ProtectedRoute>
                <Layout><Monitor /></Layout>
              </ProtectedRoute>
            } />
            {/* IngestionMonitor deprecated — redirect to IngestionActivity */}
            <Route path="/monitor/ingestion" element={
              <Navigate to="/monitor/activity" replace />
            } />
            <Route path="/monitor/activity" element={
              <ProtectedRoute>
                <Layout><IngestionActivity /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/ingestion/create" element={
              <ProtectedRoute>
                <Layout><IngestionWizard /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/ingestion/jobs/:jobId" element={
              <ProtectedRoute>
                <Layout><IngestionJobDetail /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/ingestion/jobs" element={
              <ProtectedRoute>
                <Layout><IngestionJobs /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/ingestion/control" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><IngestionControl /></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/ingestion/wizard/:jobId" element={
              <ProtectedRoute>
                <Layout><IngestionWizard /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/ingestion/wizard" element={
              <ProtectedRoute>
                <Layout><IngestionWizard /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/datastores/:name/browse" element={
              <ProtectedRoute>
                <Layout>
                  <AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}>
                    <DatastoreBrowser />
                  </Suspense></AppErrorBoundary>
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/datastores" element={
              <ProtectedRoute>
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><DataStoresPage /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/services" element={
              <ProtectedRoute>
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><PlatformServices /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/services/:componentId" element={
              <ProtectedRoute>
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><ServiceDetail /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/key-management" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><KeyManagement /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            {/* ADR-013 — internal design documentation and first-party source.
                Admins only, and the Lambda re-checks the group itself.
                K01-K04 (Story #5484 Stream 12); K05-K07 land in Stream 13. */}
            <Route path="/knowledge" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><KnowledgeHome /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/knowledge/architecture" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><KnowledgeArchitecture /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/knowledge/decisions" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><KnowledgeDecisions /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/knowledge/diagrams" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><KnowledgeDiagrams /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            {/* K08 (Story #5484 Stream 14, Task #5555): approval-gated exports. */}
            <Route path="/knowledge/exports" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><KnowledgeExports /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            {/* K09 (Story #5484 Stream 14, Task #5555): read/test/publish/recover-only Help records. */}
            <Route path="/knowledge/help-records" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><KnowledgeHelpRecords /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            {/* K05-K07 (Story #5484 Stream 13) */}
            <Route path="/knowledge/runbooks" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><KnowledgeRunbooks /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/knowledge/guides" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><KnowledgeGuides /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/knowledge/evidence" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><KnowledgeEvidence /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            {/* W5 Stream 3: legacy recovery bookmarks preserve intent while the
                canonical Admin-gated pages live under /operations/recovery. */}
            <Route path="/backup" element={<Navigate to="/operations/recovery/backups" replace />} />
            <Route path="/backup/catalogue" element={<Navigate to="/operations/recovery/points" replace />} />
            <Route path="/backup/vaults" element={<Navigate to="/operations/recovery/vaults" replace />} />
            {/* W4: consolidated tabbed hub routes (ADDITIVE — the sibling routes
                above/below are kept intact as deep-links + route-manifest anchors).
                Guard matches the strictest sibling: Backups + Ingestion expose
                admin-gated surfaces → Admins; Billing siblings are auth-only. */}
            <Route path="/backups" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><BackupsHub /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/ingestion" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><IngestionHub /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            {/* Story #4869: Admins-only. The Costs tab now shows the WHOLE
                organisation's spend read from the payer account, including Audit
                and Log Archive, which leaks org topology and relative activity
                levels. /users and /organizations already gate on Admins; billing
                was auth-only, so a Monitors user could read it. */}
            <Route path="/billing" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><CostsPricingHub /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/access" element={
              <ProtectedRoute>
                <Layout><AccessControl /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/portal-users" element={
              <ProtectedRoute>
                <Layout><PortalUsersPage /></Layout>
              </ProtectedRoute>
            } />
            {/* Story #5484 (W7) Stream 1, Task #5540: /access/organizations and
                /organisations are superseded by the canonical
                /customers/organisations screen. Their own page components
                (pages/Organizations.tsx, components/organizations/
                OrganisationsPage.tsx) are unreferenced by any other route
                as of this change and are removed with the route, not left
                as dead imports. /organisations/:id (below, untouched) keeps
                rendering OrganisationDetail -- out of this task's scope.
                The `fromLegacy` query param lets the destination show a
                visible "you were redirected" banner instead of silently
                landing the operator somewhere unexpected. */}
            <Route path="/access/organizations" element={<Navigate to="/customers/organisations?fromLegacy=access-organizations" replace />} />
            <Route path="/organisations" element={<Navigate to="/customers/organisations?fromLegacy=organisations" replace />} />
            <Route path="/organisations/:id" element={
              <ProtectedRoute>
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin" /></div>}><OrganisationDetail /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/access/users" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AccessUsers /></Layout>
              </ProtectedRoute>
            } />
            {/* W3 (Story #5353): Administration > Identity screens A02-A05.
                Admins-only at the route level; the identity Lambda's own
                persona/capability gates (ADR-019) enforce per-control access
                beneath that. */}
            <Route path="/administration/identity/users" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><AdministrationIdentityUsers /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/administration/identity/invitations" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><AdministrationIdentityInvitations /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/administration/identity/access" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><AdministrationIdentityAccess /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/administration/identity/reviews" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><AdministrationIdentityReviews /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/administration/identity/providers" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><AdministrationIdentityProviders /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/administration/identity/service-identities" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><AdministrationIdentityServiceIdentities /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            {/* Story #5484 Streams 7-8 (Tasks #5547-#5548): A01 Overview,
                A12 Security posture, A13 Security policies. Admins-only at
                the route level; the security Lambda's own persona/capability
                gates enforce per-control access beneath that. */}
            <Route path="/administration" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><AdministrationOverview /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/administration/security/posture" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><AdministrationSecurityPosture /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/administration/security/policies" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><AdministrationSecurityPolicies /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            {/* Story #5484 Streams 7-8 (Task #5548): A14 Encryption keys, A15
                Break-glass -- APPROVAL-GATED under decision #5284. */}
            <Route path="/administration/security/keys" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><AdministrationSecurityKeys /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/administration/security/break-glass" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><AdministrationSecurityBreakGlass /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            {/* Story #5484 (Launch W7) Stream 9: Administration > Platform settings A16-A19. */}
            <Route path="/administration/platform/registries" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><AdministrationPlatformRegistries /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/administration/platform/environment-templates" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><AdministrationPlatformEnvironmentTemplates /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/administration/platform/feature-flags" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><AdministrationPlatformFeatureFlags /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/administration/platform/managed-writers" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><AdministrationPlatformManagedWriters /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            {/* Story #5484 (Launch W7) Stream 10: Administration > Developer workspaces / Integrations A21/A22. */}
            <Route path="/administration/developer-workspaces" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><AdministrationDeveloperWorkspaces /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/administration/integrations" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><AdministrationIntegrations /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            {/* Story #5484 (Launch W7) Stream 5, Task #5544: C07 Support --
                a Customers-domain projection of the canonical support writer,
                Admins-only write (escalate-to-operations) enforced server-side. */}
            <Route path="/customers/support" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><CustomerSupport /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            {/* C08 Access projection -- read-only, no requiredGroup (matches
                the P18-P21 read-only projection convention above: every
                authenticated persona except Unauthorised may view). Primary
                route matches CustomersNav.tsx's canonical tab
                (/customers/access-projection, Stream 2's shared nav landed
                after this screen was first built); the organisation-scoped
                path is kept as a deep link into the same screen. */}
            <Route path="/customers/access-projection" element={
              <ProtectedRoute>
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><CustomerAccessProjection /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/customers/organisations/:organisationId/access" element={
              <ProtectedRoute>
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><CustomerAccessProjection /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            {/* P18-P21 (Story #5353 Stream 8): read-only projections, every
                persona except Unauthorised may view -- no requiredGroup,
                matching CTL-P18/19/20/21's grant_mode. */}
            <Route path="/portals/:portal/access/users" element={
              <ProtectedRoute>
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><PortalAccessUsers /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/portals/:portal/access/organisations" element={
              <ProtectedRoute>
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><PortalAccessOrganisations /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/portals/:portal/access/invitations" element={
              <ProtectedRoute>
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><PortalAccessInvitations /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/portals/:portal/access/service-identities" element={
              <ProtectedRoute>
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><PortalAccessServiceIdentities /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            {/* M04 (Story #5353 Stream 12): self-only, any authenticated
                persona -- no requiredGroup, matching CTL-M04-*'s grant_mode
                (self_only / any persona, never Admins-only). */}
            <Route path="/my-account/preferences" element={
              <ProtectedRoute>
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><MyAccountPreferences /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            {/* M01 (Story #5353 Stream 9): self-only, any authenticated
                persona, matching CTL-M01-*'s grant_mode (self_only / any
                persona, never Admins-only). */}
            <Route path="/my-account/profile" element={
              <ProtectedRoute>
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><MyAccountProfile /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            {/* M02 (Story #5353 Stream 10): self-only, any authenticated
                persona, matching CTL-M02-*'s grant_mode. */}
            <Route path="/my-account/security" element={
              <ProtectedRoute>
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><MyAccountSecurity /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            {/* M03 (Story #5353 Stream 11): self-only, any authenticated
                persona, matching CTL-M03-*'s grant_mode. */}
            <Route path="/my-account/sessions" element={
              <ProtectedRoute>
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><MyAccountSessions /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            {/* M05 (Story #5353 Stream 13a): self-only, any authenticated
                persona, matching CTL-M05-*'s grant_mode. */}
            <Route path="/my-account/notifications" element={
              <ProtectedRoute>
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><MyAccountNotifications /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            {/* M06 (Story #5353 Stream 13b): self-only, any authenticated
                persona, matching CTL-M06-*'s grant_mode. */}
            <Route path="/my-account/effective-access" element={
              <ProtectedRoute>
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><MyAccountEffectiveAccess /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            {/* Polish wave: consolidated Users & Organisations hub. Guard matches
                the strictest sibling (/access/users → Admins); the sibling routes
                above/below stay intact as deep-links. */}
            <Route path="/users-orgs" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><UsersOrgsHub /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/aemo" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AemoIngestion /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/energy-organisations" element={
              <ProtectedRoute>
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><EnergyOrganisations /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/crm" element={
              <ProtectedRoute>
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><CRMDashboard /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            {/* Story #5484 (W7) Stream 2, Task #5541: /crm/interactions is
                superseded by the canonical /customers/interactions screen
                (C04). InteractionsPage.tsx is unreferenced by any other
                route as of this change and is removed with the route, not
                left as a dead import -- same precedent Task #5540 set for
                /organisations above. The `fromLegacy` query param lets the
                destination show a visible "you were redirected" banner. */}
            <Route path="/crm/interactions" element={<Navigate to="/customers/interactions?fromLegacy=crm-interactions" replace />} />
            <Route path="/crm/documents" element={
              <ProtectedRoute>
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><DocumentsPage /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            {/* Network/Connections removed — cross-instance feature violates the
                Management CRM isolation requirement. Redirect any stale link. */}
            <Route path="/crm/network" element={<Navigate to="/crm" replace />} />
            <Route path="/crm/sharing" element={
              <ProtectedRoute>
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><SharingPage /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/contacts" element={<Navigate to="/crm" replace />} />
            <Route path="/contacts/people" element={
              <ProtectedRoute>
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><ContactsList /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/contacts/people/:id" element={
              <ProtectedRoute>
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><ContactDetail /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/access/:section" element={
              <ProtectedRoute>
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><AccessControlDetail /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            {/* O23 Logs. The contract's canonical path is /operations/logs and
                the page was only ever mounted at /logs, patched over with a
                ROUTE_ALIASES entry so the chrome could still identify it. That
                alias fixed the chrome and not the navigation: O23 sits in the
                "Estate operations and durable work" group, so DomainTabs drew a
                Logs tab on EVERY Operations screen pointing at
                /operations/logs -- a route that did not exist, landing every
                click on the catch-all NotFound. Mount the canonical path (the
                alias is gone with it) and keep /logs as a redirect so existing
                links and bookmarks survive. */}
            <Route path="/operations/logs" element={
              <ProtectedRoute>
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><Logs /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/logs" element={<Navigate to="/operations/logs" replace />} />
            {/* Story #5650: the four demo screens are RESTORED. The 2026-09-02
                withdrawal said "no read route exists for them in
                lambda/dashboard/demo-env", and for two of the four that was
                never true — the reasons were written from a reading of the
                register, not of the backend:

                  Scenarios  the catalogue is in the AU vault at
                             env-control/scenarios/*.json (hero-pair.json has
                             been there since July); handler.py already loaded
                             one by name, so listing the prefix is the read.
                  Evidence   every demo-env mutation has been audited to
                             secureembed-management-audit-logs since July —
                             127 rows of real scenario resets and baseline
                             restores, with actor, time and outcome.

                Readiness and Resets genuinely needed new read models; those
                are now GET /readiness and GET /reset-preview in handler.py.
                All four make a real network call and render real data. */}
            {[
              ['/operations/audit', OperationsAudit],
              ['/operations/demo/holds', DemoHolds],
              ['/operations/demo/scenarios', DemoScenarios],
              ['/operations/demo/readiness', DemoReadiness],
              ['/operations/demo/resets', DemoResets],
              ['/operations/demo/evidence', DemoEvidence],
            ].map(([path, Page]) => <Route key={path as string} path={path as string} element={<ProtectedRoute requiredGroup="Admins"><Layout><AppErrorBoundary><Suspense fallback={<Loader2 className="w-8 h-8 animate-spin" />}><Page /></Suspense></AppErrorBoundary></Layout></ProtectedRoute>} />)}
            <Route path="/support" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><SupportTickets /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/portals" element={
              <ProtectedRoute requiredGroup={['Admins', 'Monitors']}>
                <Layout>{isPortalsWorkspaceEnabled()
                  ? <AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><Portals /></Suspense></AppErrorBoundary>
                  : <AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><Portals /></Suspense></AppErrorBoundary>}</Layout>
              </ProtectedRoute>
            } />
            <Route path="/portals/:portalId/select" element={
              <ProtectedRoute requiredGroup={['Admins', 'Monitors']}>
                <Layout>{isPortalsWorkspaceEnabled() ? <AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><PortalEnvironmentSelect /></Suspense></AppErrorBoundary> : null}</Layout>
              </ProtectedRoute>
            } />
            <Route path="/portals/:portalId/overview" element={
              <ProtectedRoute requiredGroup={['Admins', 'Monitors']}>
                <Layout>{isPortalsWorkspaceEnabled() ? <div data-testid="portals-workspace-placeholder"><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><PortalOverview /></Suspense></AppErrorBoundary></div> : null}</Layout>
              </ProtectedRoute>
            } />
            <Route path="/portals/:portalId/environments" element={
              <ProtectedRoute requiredGroup={['Admins', 'Monitors']}>
                <Layout>{isPortalsWorkspaceEnabled() ? <AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><PortalEnvironments /></Suspense></AppErrorBoundary> : null}</Layout>
              </ProtectedRoute>
            } />
            <Route path="/portals/:portalId/deployments" element={
              <ProtectedRoute requiredGroup={['Admins', 'Monitors']}>
                <Layout>{isPortalsWorkspaceEnabled() ? <AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><PortalDeployments /></Suspense></AppErrorBoundary> : null}</Layout>
              </ProtectedRoute>
            } />
            <Route path="/portals/:portalId/configuration/effective" element={
              <ProtectedRoute requiredGroup={['Admins', 'Monitors']}>
                <Layout>{isPortalsWorkspaceEnabled() ? <AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><PortalConfigurationEffective /></Suspense></AppErrorBoundary> : null}</Layout>
              </ProtectedRoute>
            } />
            <Route path="/portals/:portalId/documentation" element={
              <ProtectedRoute requiredGroup={['Admins', 'Monitors']}>
                <Layout>{isPortalsWorkspaceEnabled() ? <AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><PortalDocumentation /></Suspense></AppErrorBoundary> : null}</Layout>
              </ProtectedRoute>
            } />
            <Route path="/portals/:portalId/history" element={
              <ProtectedRoute requiredGroup={['Admins', 'Monitors']}>
                <Layout>{isPortalsWorkspaceEnabled() ? <AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><PortalHistory /></Suspense></AppErrorBoundary> : null}</Layout>
              </ProtectedRoute>
            } />
            <Route path="/portals/:portalId/settings" element={
              <ProtectedRoute>
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><PortalSettings /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            {/* Story #5164 (Story #5010 Phase 4): Bedrock Model Configuration
                admin console. Admins-only, matching the model-config Lambda's
                own require_admin gate. */}
            {/* Story #5346 Stream 1c: A20 supersedes this route -- redirect,
                never 404. ModelConfiguration.tsx and its own tests stay on
                disk unchanged (pattern: see /environment-control below). */}
            <Route path="/model-config" element={<Navigate to="/administration/ai-model-defaults" replace />} />
            <Route path="/administration/ai-model-defaults" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><AiModelDefaults /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            {/* Story #5346 (Launch W4) Stream 1b: P08/P09 -- the launch
                console's new AI-models screens. Portal is chosen via
                ModelConfigPortalProvider's URL ?portal=/localStorage
                mechanism (see that context's docstring), not a URL path
                segment -- the W1 shell's portal-scoped nested route
                (/portals/:portalId/configuration/ai-models) lands once that
                shell is flag-enabled; these routes stay stable either way. */}
            {/* Story #5343 (2026-09-02 design-match audit): P07-P11 are
                contracted as portal-scoped -- /portals/{portalId}/configuration/...
                -- but shipped mounted flat at /configuration/*, which the audit
                recorded as "UNSCOPED ROUTE" on each of the five. Unscoped, the
                shared chrome could not render on them at all: its Focus line
                reads "exact portal and environment" and the URL named neither.
                These are the canonical mounts; the flat paths below now redirect
                here so existing links and bookmarks keep working. */}
            <Route path="/portals/:portalId/configuration/general" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><ConfigurationGeneral /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/portals/:portalId/configuration/ai-models" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><ConfigurationAiModels /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/portals/:portalId/configuration/ai-models/compare" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><AiModelCompare /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/portals/:portalId/configuration/plans-pricing" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><ConfigurationPlansPricing /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/portals/:portalId/configuration/integrations" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><ConfigurationIntegrations /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/configuration/ai-models" element={<Navigate to="/portals/innovateflow/configuration/ai-models" replace />} />
            <Route path="/configuration/ai-models/compare" element={<Navigate to="/portals/innovateflow/configuration/ai-models/compare" replace />} />
            <Route path="/developer-vms" element={
              <ProtectedRoute>
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><DeveloperVMs /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/developer-vms/:vmId" element={
              <ProtectedRoute>
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><DeveloperVMDetail /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            {/* Story #5483 (W6) Stream 1.1: ENV_RELEASES_V2 read at render time
                (module-scope const above is fine for Vite's build-time inline,
                but keeping the branch here mirrors isW1Shell()'s render-time
                convention so route-manifest.test.tsx's static App import never
                captures a stale value). Off by default -- EnvironmentsHub/
                ReleasesHub keep rendering byte-for-byte unchanged. */}
            <Route path="/environments" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}>{ENV_RELEASES_V2 ? <EnvironmentFleet /> : <EnvironmentsHub />}</Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            {/* E02 is an additive canonical route. It deliberately sits outside
                the VITE_ENV_RELEASES_V2 replacement block: explicit IDs in the
                query string are the comparison's complete scope. */}
            <Route path="/environments/compare" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><EnvironmentCompare /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            {/* Story #5483 (W6) Stream 1.4: E03 Plan environment -- additive,
                no legacy equivalent, gated behind ENV_RELEASES_V2 like every
                other Stream 1+ route (ground rule 9). */}
            {ENV_RELEASES_V2 && <Route path="/environments/new" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><EnvironmentPlanNew /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />}
            {/* Story #5483 (W6) Stream 2/3: E04-E11 nested per-environment
                projections. Additive routes, gated behind ENV_RELEASES_V2
                like every other Stream 1+ route (ground rule 9) -- there is
                no legacy equivalent to fall back to, so the flag being off
                simply 404s (Navigate to the fleet) rather than rendering.
                E04/E05/E07-E11 renamed from their original /e0N numbering to
                the Screen Contract's canonical named paths (Task #5529,
                Stream 12) -- Stream 6 had already made this same switch for
                E16-E18 below; this brings E04-E11 in line rather than
                leaving two conventions live. E06 is NOT renamed here: its
                canonical path is /services/{serviceId}, but the real
                implementation identifies a service by a (serviceType, name)
                query-string pair -- there is no single path-safe serviceId
                in the data model today. Renaming the URL segment alone
                would not satisfy the canonical shape, only its surface
                text, so this one stays /e06 with the gap disclosed in
                knowledge/runbooks/w6-environment-lifecycle.md rather than
                silently declared fixed. */}
            {ENV_RELEASES_V2 && <Route path="/environments/:environmentId/overview" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><EnvironmentOverview /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />}
            {ENV_RELEASES_V2 && <Route path="/environments/:environmentId/services" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><EnvironmentServices /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />}
            {ENV_RELEASES_V2 && <Route path="/environments/:environmentId/e06" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><EnvironmentServiceDetail /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />}
            {ENV_RELEASES_V2 && <Route path="/environments/:environmentId/data" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><EnvironmentData /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />}
            {ENV_RELEASES_V2 && <Route path="/environments/:environmentId/releases" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><EnvironmentReleasesTab /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />}
            {ENV_RELEASES_V2 && <Route path="/environments/:environmentId/operations" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><EnvironmentOperationsTab /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />}
            {ENV_RELEASES_V2 && <Route path="/environments/:environmentId/cost" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><EnvironmentCostTab /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />}
            {ENV_RELEASES_V2 && <Route path="/environments/:environmentId/access" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><EnvironmentAccessTab /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />}
            {/* Story #5483 (W6) Stream 4.1/4.2/4.3: E12 lifecycle -- read-only
                display plus DEV/TEST transition plus UAT/PROD preview, all in
                one screen (EnvironmentLifecycle.tsx branches per environment). */}
            {ENV_RELEASES_V2 && <Route path="/environments/:environmentId/lifecycle" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><EnvironmentLifecycle /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />}
            {/* Story #5483 (W6) Stream 5: E13-E15 read/hold/reset,
                read/backup-hand-off and independent-verification
                projections -- additive routes, gated behind
                ENV_RELEASES_V2 like every other Stream 1+ route. Renamed
                from /e13../e15 to their canonical named paths for the same
                reason as E04-E11 above (Task #5529, Stream 12). */}
            {ENV_RELEASES_V2 && <Route path="/environments/:environmentId/operations/demo" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><EnvironmentDemoState /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />}
            {ENV_RELEASES_V2 && <Route path="/environments/:environmentId/operations/recovery" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><EnvironmentRecovery /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />}
            {ENV_RELEASES_V2 && <Route path="/environments/:environmentId/operations/verification" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><EnvironmentVerification /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />}
            {/* Story #5483 (W6) Stream 6: E16 activity, E17+E18 drift/evidence
                -- additive routes, gated behind ENV_RELEASES_V2 like every
                other Stream 1+ route. Canonical routes per the Screen
                Contract tab (not the /e16../e18 numbered convention the
                earlier streams used): /operations/activity for E16,
                /drift?view=drift|evidence for E17/E18. */}
            {ENV_RELEASES_V2 && <Route path="/environments/:environmentId/operations/activity" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><EnvironmentActivity /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />}
            {ENV_RELEASES_V2 && <Route path="/environments/:environmentId/drift" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><EnvironmentDrift /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />}
            <Route path="/environments/:env" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><EnvironmentDetail /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            {/* Story #5484 (Launch W7) Stream 1, Task #5540: C02/C03 canonical
                Customers screens. */}
            <Route path="/customers/organisations" element={
              <ProtectedRoute>
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><CustomerOrganisations /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/customers/contacts" element={
              <ProtectedRoute>
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><CustomerContacts /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            {/* Story #5484 (Launch W7) Stream 3, Task #5542: C05 canonical
                Customer Documents screen (read-only pipeline observability). */}
            <Route path="/customers/documents" element={
              <ProtectedRoute>
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><CustomerDocuments /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            {/* Story #5484 (Launch W7) Stream 4, Task #5543: C06 Customer Sharing --
                APPROVAL-GATED real external-share write, Admins only. */}
            <Route path="/customers/sharing" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><CustomerSharing /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            {/* Story #5484 (Launch W7) Stream 2, Task #5541: C01/C04
                canonical Customers screens. */}
            <Route path="/customers" element={
              <ProtectedRoute>
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><CustomerHome /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/customers/interactions" element={
              <ProtectedRoute>
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><CustomerInteractions /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/releases" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}>{ENV_RELEASES_V2 ? <ReleaseCatalogue /> : <ReleasesHub />}</Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            {/* Story #5483 (W6) Stream 7: R02-R04. Additive routes, gated
                behind ENV_RELEASES_V2 like every other Stream 1+ route --
                there is no legacy equivalent to fall back to. */}
            {ENV_RELEASES_V2 && <Route path="/releases/new" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><ReleaseCreate /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />}
            {ENV_RELEASES_V2 && <Route path="/releases/:releaseId/summary" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><ReleaseSummary /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />}
            {ENV_RELEASES_V2 && <Route path="/releases/:releaseId/changeset" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><ReleaseChangeSet /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />}
            {/* Story #5483 (W6) Stream 10: R11-R14 read-only artifact
                catalogues, gated behind ENV_RELEASES_V2 same as R02-R04. */}
            {ENV_RELEASES_V2 && <Route path="/releases/artifacts/configuration-baselines" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><ReleaseArtifactsConfig /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />}
            {ENV_RELEASES_V2 && <Route path="/releases/artifacts/data-snapshots" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><ReleaseArtifactsData /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />}
            {ENV_RELEASES_V2 && <Route path="/releases/artifacts/run-profiles" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><ReleaseArtifactsProfiles /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />}
            {ENV_RELEASES_V2 && <Route path="/releases/artifacts/provenance" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><ReleaseArtifactsProvenance /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />}
            {/* Story #5483 (W6) Stream 8: R05 gates, R06 approvals.
                Additive routes, gated behind ENV_RELEASES_V2 like every
                other Stream 1+ route. */}
            {ENV_RELEASES_V2 && <Route path="/releases/:releaseId/gates" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><ReleaseGates /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />}
            {ENV_RELEASES_V2 && <Route path="/releases/:releaseId/approvals" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><ReleaseApprovals /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />}
            {/* Story #5483 (W6) Stream 9: R07 rollout, R08 verification,
                R09 rollback, R10 history. Additive routes, gated behind
                ENV_RELEASES_V2 like every other Stream 1+ route. */}
            {ENV_RELEASES_V2 && <Route path="/releases/:releaseId/rollout" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><ReleaseRollout /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />}
            {ENV_RELEASES_V2 && <Route path="/releases/:releaseId/verification" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><ReleaseVerification /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />}
            {ENV_RELEASES_V2 && <Route path="/releases/:releaseId/rollback" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><ReleaseRollback /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />}
            {ENV_RELEASES_V2 && <Route path="/releases/:releaseId/history" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><ReleaseHistory /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />}
            <Route path="/promotion-packages" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><PromotionPackages /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            {/* RT-18: legacy Environment Control page is superseded by
                EnvironmentDetail + ReleasesHub — redirect, keep the file. */}
            <Route path="/environment-control" element={<Navigate to="/environments" replace />} />
            <Route path="/profile" element={
              <ProtectedRoute>
                <Layout><ProfilePage /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/preferences" element={
              <ProtectedRoute>
                <Layout><PreferencesPage /></Layout>
              </ProtectedRoute>
            } />
            {/* Story #4869: the /costs route was REMOVED. Nothing in the app
                ever linked to it -- navigation goes to /billing, which renders the
                same CostsPage embedded in CostsPricingHub -- and it carried only a
                bare ProtectedRoute. Gating /billing while leaving this open would
                have left a second, ungated door onto org-wide billing data, and
                once the backend Admins gate landed a Monitors user reaching it
                would have seen a raw error banner instead of /forbidden. */}
            <Route path="/pricing" element={
              <ProtectedRoute>
                <Layout><PricingConfiguration /></Layout>
              </ProtectedRoute>
            } />
            {/* Story #5346 Stream 2b: P10 (read-only projection, no writer)
                and A10 (the real writer -- same PricingConfiguration console
                reused inside AdminPricing.tsx, admin-gated since it can
                actually publish). Additive, does not touch /pricing above.
                Admins-gated (coordinator review fix): the backing Lambda
                (lambda/dashboard/pricing/handler.py) requires require_admin()
                unconditionally, including GET -- this route must match or a
                non-admin gets redirected to /forbidden only after the page
                has already rendered and its API calls have failed. */}
            <Route path="/configuration/plans-pricing" element={<Navigate to="/portals/innovateflow/configuration/plans-pricing" replace />} />
            <Route path="/administration/commercial/pricing" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><AdminPricing /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            {/* Story #5346 Stream 3b: P07 (versioned draft/ChangeSet UI,
                RBAC-gated writes) and P11 (read-only integration-binding
                projection, no local writer). Additive. */}
            <Route path="/configuration/general" element={<Navigate to="/portals/innovateflow/configuration/general" replace />} />
            {/* Admins-gated (coordinator review fix): portal-settings/handler.py
                is_admin()-gates every route including GET. */}
            <Route path="/configuration/integrations" element={<Navigate to="/portals/innovateflow/configuration/integrations" replace />} />
            {/* Story #5346 Stream 4b: P23/P24 (usage & cost) and A08 (Admin
                Cost Explorer). Admins-gated (coordinator review fix): all
                three call costsApi, backed by lambda/dashboard/costs/handler.py
                which require_admin()-gates every route including GET (same
                reasoning /billing was gated for in Story #4869 -- cost data
                leaks org topology and relative activity levels). */}
            <Route path="/usage-cost/summary" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><UsageCostSummary /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/usage-cost/trajectory" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><UsageCostTrajectory /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/administration/commercial/cost-explorer" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><AdminCostExplorer /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            {/* Story #5346 Stream 5b: P25 (Allocation) and P26 (AI roles).
                Admins-gated (coordinator review fix): allocationApi/costsApi/
                modelConfigApi backends all require_admin(). */}
            <Route path="/usage-cost/allocation" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><UsageCostAllocation /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/usage-cost/ai-roles" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><UsageCostAiRoles /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            {/* Story #5346 Stream 6c: P27 (Investigations) and A09 (Budgets).
                Admins-gated (coordinator review fix): investigations/handler.py
                require_admin()-gates every route including GET/list. */}
            <Route path="/usage-cost/investigations" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><UsageCostInvestigations /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/administration/commercial/budgets" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><AdminBudgets /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            {/* Story #5346 Stream 7b: A11 (Usage/metering). Admins-gated
                (coordinator review fix): metering/handler.py require_admin()-
                gates every route including GET/Inspect -- the stream's AC
                intent ("every role can View") was never actually wired to
                the backend, so the route must match current backend reality
                rather than the unimplemented intent. */}
            <Route path="/administration/commercial/metering" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><AdminMetering /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            {/* W1 (Story #5351): flag-gated /home workspace. isW1Shell() is called
                here, inside App's render body — never at module scope — so
                route-manifest.test.tsx's static import of App (before any config
                stub runs) never captures a stale flag value; see utils/w1Flag.ts. */}
            <Route path="/home" element={
              <ProtectedRoute>
                <Layout>{isW1Shell() ? <AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><HomeWorkspace /></Suspense></AppErrorBoundary> : <NotFound />}</Layout>
              </ProtectedRoute>
            } />
            {/* H07 Saved views. The contract gives this ONE Home view its own
                path instead of a `?view=` value, so it needs its own route;
                HomeWorkspace reads the pathname and selects the saved-views
                tab. Without it the contract URL 404'd and H07 could not
                resolve to its own contract row. */}
            <Route path="/home/views" element={
              <ProtectedRoute>
                <Layout>{isW1Shell() ? <AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><HomeWorkspace /></Suspense></AppErrorBoundary> : <NotFound />}</Layout>
              </ProtectedRoute>
            } />
            <Route path="/home/attention/:itemId" element={
              <ProtectedRoute>
                <Layout>{isW1Shell() ? <AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><AttentionDetail /></Suspense></AppErrorBoundary> : <NotFound />}</Layout>
              </ProtectedRoute>
            } />
            {/* W1 (Story #5351 W1-T16): flag-gated /search (Screen Contract
                G03). Open to any authenticated user — the page itself gates
                each result group by the SAME requiredGroup its own route
                uses (e.g. Knowledge results only query when the signed-in
                user is in Admins), so there is no separate route-level gate
                to duplicate here. */}
            <Route path="/search" element={
              <ProtectedRoute>
                <Layout>{isW1Shell() ? <AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><SearchCommand /></Suspense></AppErrorBoundary> : <NotFound />}</Layout>
              </ProtectedRoute>
            } />
            {/* W1 (Story #5351 W1-T18): /dev/fixtures — INSIDE
                ProtectedRoute requiredGroup="Admins" AND flag-gated (a
                config flag alone is browser-mutable, not an auth control;
                Red-Team Amendment, S8 fixtures gating). A non-Admin hits
                the SAME /forbidden redirect every other Admins-only route
                uses; flag OFF renders the same real 404 as /home and
                /search. */}
            <Route path="/dev/fixtures" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout>{isW1Shell() ? <AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><Fixtures /></Suspense></AppErrorBoundary> : <NotFound />}</Layout>
              </ProtectedRoute>
            } />
            {/* Legacy route redirects */}
            <Route path="/deploy" element={<Navigate to="/" replace />} />
            <Route path="/infrastructure" element={<Navigate to="/" replace />} />

            {/* W5 Stream 1 (Story #5352, Task #5424): Operations O01-O06 —
                exception-first estate control room. /operations used to be a
                dead redirect to home; it is now a real 6-screen route group,
                same Admins-only gate as every sibling operational surface. */}
            <Route path="/operations" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><CommandCentre /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/operations/attention" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><Attention /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/operations/jobs" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><Jobs /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/operations/approvals" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><Approvals /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/operations/schedules" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><Schedules /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/operations/incidents" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><Incidents /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />

            {/* W5 Stream 2 (Story #5352, Tasks #5434-#5438): Data Flows O07-O11
                — read-only consolidation of the existing AemoIngestion/
                IngestionControl/IngestionHub feature set under one canonical
                contract. Same Admins-only gate as every sibling Operations
                surface above. */}
            <Route path="/operations/data-flows" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><DataFlowsOverview /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/operations/data-flows/jobs" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><DataFlowsJobs /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/operations/data-flows/sources" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><DataFlowsSources /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/operations/data-flows/schedules" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><DataFlowsSchedules /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/operations/data-flows/activity" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><DataFlowsActivity /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />

            {/* W5 Stream 3 (Story #5352): Recovery O12-O17. Every canonical
                route is explicitly Admin-gated; proposal-only pages contain no
                restore/start command imports. */}
            <Route path="/operations/recovery/backups" element={<ProtectedRoute requiredGroup="Admins"><Layout><AppErrorBoundary><Suspense fallback={<Loader2 className="m-auto w-8 h-8 animate-spin" />}><BackupRestore /></Suspense></AppErrorBoundary></Layout></ProtectedRoute>} />
            <Route path="/operations/recovery/points" element={<ProtectedRoute requiredGroup="Admins"><Layout><AppErrorBoundary><Suspense fallback={<Loader2 className="m-auto w-8 h-8 animate-spin" />}><BackupCatalogue /></Suspense></AppErrorBoundary></Layout></ProtectedRoute>} />
            <Route path="/operations/recovery/restore-plans" element={<ProtectedRoute requiredGroup="Admins"><Layout><AppErrorBoundary><Suspense fallback={<Loader2 className="m-auto w-8 h-8 animate-spin" />}><RestorePlans /></Suspense></AppErrorBoundary></Layout></ProtectedRoute>} />
            <Route path="/operations/recovery/vaults" element={<ProtectedRoute requiredGroup="Admins"><Layout><AppErrorBoundary><Suspense fallback={<Loader2 className="m-auto w-8 h-8 animate-spin" />}><VaultsDR /></Suspense></AppErrorBoundary></Layout></ProtectedRoute>} />
            <Route path="/operations/recovery/rehearsals" element={<ProtectedRoute requiredGroup="Admins"><Layout><AppErrorBoundary><Suspense fallback={<Loader2 className="m-auto w-8 h-8 animate-spin" />}><RecoveryRehearsals /></Suspense></AppErrorBoundary></Layout></ProtectedRoute>} />
            <Route path="/operations/recovery/evidence" element={<ProtectedRoute requiredGroup="Admins"><Layout><AppErrorBoundary><Suspense fallback={<Loader2 className="m-auto w-8 h-8 animate-spin" />}><RecoveryEvidence /></Suspense></AppErrorBoundary></Layout></ProtectedRoute>} />

            {/* Catch-all: a real 404 (W1a / RT-10) instead of a silent redirect
                to '/', so a dropped/broken route is visible rather than masked. */}
            <Route path="*" element={
              <ProtectedRoute>
                <Layout><NotFound /></Layout>
              </ProtectedRoute>
            } />
          </Routes>
        </ModelConfigPortalProvider>
        </EnvironmentProvider>
        </ScopeProvider>
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
};

// Re-exported for compatibility — pages/Profile.tsx imports useAuth from '../App';
// __tests__/route-manifest.test.tsx imports navGroups/allNavHrefs from '../App'.
export { useAuth } from './contexts/AuthContext';
export { navGroups, allNavHrefs };
export default App;
